import React, { useState, useEffect } from 'react';
import { Form, Formik } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { useBrand } from '../../hooks/useBrand';
import styled from 'styled-components';
import { useAppDispatch } from '../../hooks';
import { setProfile, fetchProfile, patientLogin } from '../../reducers/user';
import { setAuthToken } from '../../utils/auth';
import { hasUserAgreedToTerms, setTermsAgreementStatus, getTermsAgreementStatus } from '../../utils/termsAgreement';
import TermsAndConditionsModal from '../../components/TermsAndConditionsModal';
import * as api from '../../services/api';

const validationSchema = yup.object({
  email: yup.string().required('Email or username is required').trim(),
  password: yup.string().required('Password is required'),
});

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  padding: 24px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 400px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 40px;
`;

const LogoImg = styled.img`
  display: block;
  margin: 0 auto 32px;
  max-width: 180px;
  height: auto;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  text-align: center;
  margin: 0 0 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  font-size: 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-sizing: border-box;
  margin-bottom: 16px;
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`;

const ErrorText = styled.div`
  color: #dc2626;
  font-size: 12px;
  margin-top: -8px;
  margin-bottom: 12px;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 12px 20px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  background: #2563eb;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 8px;
  &:hover {
    background: #1d4ed8;
  }
`;

const ForgotLink = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 16px;
  font-size: 14px;
  color: #6b7280;
  text-decoration: none;
  &:hover {
    color: #374151;
    text-decoration: underline;
  }
`;

const TermsLink = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  &:hover {
    text-decoration: underline;
  }
`;

const Terms = styled.p`
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
  margin: 24px 0 0;
  line-height: 1.5;
`;

const BackLink = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 12px;
  font-size: 14px;
  color: #6b7280;
  text-decoration: none;
  &:hover {
    color: #374151;
  }
`;

const TabBar = styled.div`
  display: flex;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 24px;
`;

const Tab = styled.button<{ active?: boolean }>`
  flex: 1;
  padding: 10px 0;
  font-size: 14px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  background: ${({ active }) => (active ? '#2563eb' : '#f9fafb')};
  color: ${({ active }) => (active ? 'white' : '#6b7280')};
  transition: background 0.15s, color 0.15s;
  &:hover {
    background: ${({ active }) => (active ? '#1d4ed8' : '#f3f4f6')};
  }
`;

const mfaCodeSchema = yup.object({
  code: yup.string().required('Code is required').min(6, '6-digit code or 8-digit backup code'),
});

const patientLoginSchema = yup.object({
  patient_uuid: yup.string().required('Patient ID is required').trim(),
  password: yup.string().required('Password is required'),
});

const BRAND_CONFIG = {
  neuroiq: { logo: '/neuroiq-logo.svg', title: 'NeuroIQ', alt: 'NeuroIQ' },
  default: { logo: '/logo.svg', title: 'DrSchmoe.ai', alt: 'DrSchmoe.ai' },
};

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const brand = useBrand();
  const { logo, title, alt } = BRAND_CONFIG[brand];
  const [loginMode, setLoginMode] = useState<'provider' | 'patient'>('provider');
  const [error, setError] = useState('');
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaEmail, setMfaEmail] = useState('');
  const [patientMfaStep, setPatientMfaStep] = useState(false);
  const [pendingPatientUuid, setPendingPatientUuid] = useState('');
  const [pendingPatientPassword, setPendingPatientPassword] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsVersion, setTermsVersion] = useState('1.2');

  useEffect(() => {
    document.title = title;
  }, [title]);

  useEffect(() => {
    api.getTermsVersion().then((res) => {
      if (res?.success && res?.data?.terms_version) setTermsVersion(res.data.terms_version);
    }).catch(() => {});
  }, []);

  const handlePasswordSubmit = async (email: string, password: string) => {
    setError('');
    const terms = getTermsAgreementStatus();
    try {
      const res = await api.login(
        email,
        password,
        terms?.timestamp
          ? {
              terms_version: terms.version || '1.0',
              consent_timestamp: terms.timestamp,
              browser_user_agent: terms.browserUserAgent,
              browser_language: terms.browserLanguage,
              browser_platform: terms.browserPlatform,
              consent_data: terms.consentData,
            }
          : undefined
      );
      if (res?.code === 'terms_consent_required') {
        setError('You must accept the Terms and Conditions to sign in.');
        setShowTermsModal(true);
        return;
      }
      if (res?.data?.mfa_verification_required || res?.code === 'mfa_verification_required') {
        const emailForMfa = res?.data?.email || email;
        setMfaEmail(emailForMfa);
        setMfaStep(true);
        return;
      }
      if (res?.success && res?.data?.auth_token) {
        setAuthToken(res.data.auth_token);
        dispatch(setProfile({
          id: res.data.user?.id,
          email: res.data.user?.email,
          first_name: res.data.user?.first_name || '',
          last_name: res.data.user?.last_name || '',
          isAllowedAdmin: res.data?.isAllowedAdmin,
        }));
        dispatch(fetchProfile());
        if (res?.data?.mfa_setup_required || res?.code === 'mfa_setup_required') {
          sessionStorage.setItem('mfa_setup_required', 'true');
          navigate(brand === 'neuroiq' ? '/neuroiq' : '/dashboard');
          return;
        }
        sessionStorage.removeItem('mfa_setup_required');
        navigate(brand === 'neuroiq' ? '/neuroiq' : '/dashboard');
        return;
      }
      setError(res?.message || 'Login failed');
    } catch (err: any) {
      const data = err?.data || err;
      if (data?.code === 'terms_consent_required') {
        setError('You must accept the Terms and Conditions to sign in.');
        setShowTermsModal(true);
        return;
      }
      if (data?.data?.mfa_verification_required || data?.code === 'mfa_verification_required') {
        setMfaEmail(data?.data?.email || email);
        setMfaStep(true);
        return;
      }
      setError(data?.message || err?.message || 'Login failed');
    }
  };

  const handleMfaSubmit = async (code: string) => {
    setError('');
    try {
      const res = await api.mfaVerifyLogin(mfaEmail, code);
      if (res?.success && res?.data?.auth_token) {
        setAuthToken(res.data.auth_token);
        dispatch(setProfile({
          id: res.data.user?.id,
          email: res.data.user?.email,
          first_name: res.data.user?.first_name || '',
          last_name: res.data.user?.last_name || '',
          isAllowedAdmin: res.data.isAllowedAdmin,
        }));
        dispatch(fetchProfile());
        navigate(brand === 'neuroiq' ? '/neuroiq' : '/dashboard');
        return;
      }
      setError(res?.message || 'Invalid code');
    } catch (err: any) {
      setError(err?.data?.message || err?.message || 'Invalid code');
    }
  };

  const handlePatientLoginSubmit = async (patientUuid: string, password: string, totpCode?: string) => {
    setError('');
    try {
      const res = await dispatch(patientLogin({
        patient_uuid: patientUuid.trim(),
        password,
        ...(totpCode ? { totp_code: totpCode } : {}),
      })).unwrap();
      if (res?.mfa_required && !res?.auth_token) {
        setPendingPatientUuid(patientUuid.trim());
        setPendingPatientPassword(password);
        setPatientMfaStep(true);
        return;
      }
      navigate(brand === 'neuroiq' ? '/neuroiq' : '/dashboard');
    } catch (err: any) {
      const data = err?.data || err;
      if (data?.mfa_verification_required || data?.code === 'mfa_verification_required') {
        setPendingPatientUuid(patientUuid.trim());
        setPendingPatientPassword(password);
        setPatientMfaStep(true);
        return;
      }
      setError(data?.message || err?.message || 'Login failed');
    }
  };

  return (
    <Wrapper>
      <Card>
        <LogoImg src={logo} alt={alt} />
        <Title>{mfaStep || patientMfaStep ? 'MFA Code' : 'Sign In'}</Title>
        {!mfaStep && !patientMfaStep && (
          <TabBar>
            <Tab active={loginMode === 'provider'} onClick={() => { setLoginMode('provider'); setError(''); }}>Provider</Tab>
            <Tab active={loginMode === 'patient'} onClick={() => { setLoginMode('patient'); setError(''); }}>Patient</Tab>
          </TabBar>
        )}
        {loginMode === 'provider' && !mfaStep && (
          <Formik
            initialValues={{ email: '', password: '' }}
            validationSchema={validationSchema}
            onSubmit={({ email, password }) => handlePasswordSubmit(email, password)}
          >
            {({ values, errors, submitCount, handleChange, handleBlur }) => (
              <Form>
                {error && <ErrorText style={{ marginBottom: 12 }}>{error}</ErrorText>}
                <Label htmlFor="email">Email or username</Label>
                <Input
                  id="email"
                  type="text"
                  name="email"
                  placeholder="Email or username"
                  value={values.email ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {submitCount > 0 && errors.email && <ErrorText>{errors.email}</ErrorText>}
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={values.password ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {submitCount > 0 && errors.password && <ErrorText>{errors.password}</ErrorText>}
                <SubmitButton type="submit">Sign In</SubmitButton>
                <Terms>
                  By signing in you agree to our{' '}
                  <TermsLink type="button" onClick={() => setShowTermsModal(true)}>
                    Terms &amp; Conditions
                  </TermsLink>
                  . You must agree before logging in.
                </Terms>
                <div style={{ textAlign: 'center', marginTop: 8 }}>
                  <BackLink to="/forgot-password" style={{ fontSize: 13, color: '#6b7280' }}>Forgot password?</BackLink>
                </div>
                <BackLink to="/">Back to home</BackLink>
              </Form>
            )}
          </Formik>
        )}
        {loginMode === 'provider' && mfaStep && (
          <Formik
            initialValues={{ code: '' }}
            validationSchema={mfaCodeSchema}
            onSubmit={({ code }) => handleMfaSubmit(code)}
          >
            {({ values, errors, submitCount, handleChange, handleBlur }) => (
              <Form>
                {error && <ErrorText style={{ marginBottom: 12 }}>{error}</ErrorText>}
                <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 12 }}>Enter the 6-digit code from your authenticator app (or 8-digit backup code) for {mfaEmail}</p>
                <Label htmlFor="code">Code</Label>
                <Input
                  id="code"
                  type="text"
                  name="code"
                  placeholder="000000"
                  value={values.code ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="one-time-code"
                />
                {submitCount > 0 && errors.code && <ErrorText>{errors.code}</ErrorText>}
                <SubmitButton type="submit">Verify</SubmitButton>
                <TermsLink type="button" onClick={() => { setMfaStep(false); setError(''); }} style={{ display: 'block', marginTop: 12, background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 14 }}>
                  Use different account
                </TermsLink>
                <BackLink to="/">Back to home</BackLink>
              </Form>
            )}
          </Formik>
        )}
        {loginMode === 'patient' && !patientMfaStep && (
          <Formik
            initialValues={{ patient_uuid: '', password: '' }}
            validationSchema={patientLoginSchema}
            onSubmit={({ patient_uuid, password }) => handlePatientLoginSubmit(patient_uuid, password)}
          >
            {({ values, errors, submitCount, handleChange, handleBlur }) => (
              <Form>
                {error && <ErrorText style={{ marginBottom: 12 }}>{error}</ErrorText>}
                <Label htmlFor="patient_uuid">Patient ID</Label>
                <Input
                  id="patient_uuid"
                  type="text"
                  name="patient_uuid"
                  placeholder="Enter your patient ID"
                  value={values.patient_uuid ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {submitCount > 0 && errors.patient_uuid && <ErrorText>{errors.patient_uuid}</ErrorText>}
                <Label htmlFor="patient_password">Password</Label>
                <Input
                  id="patient_password"
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={values.password ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {submitCount > 0 && errors.password && <ErrorText>{errors.password}</ErrorText>}
                <SubmitButton type="submit">Sign In</SubmitButton>
                <BackLink to="/">Back to home</BackLink>
              </Form>
            )}
          </Formik>
        )}
        {loginMode === 'patient' && patientMfaStep && (
          <Formik
            initialValues={{ code: '' }}
            validationSchema={mfaCodeSchema}
            onSubmit={({ code }) => handlePatientLoginSubmit(pendingPatientUuid, pendingPatientPassword, code)}
          >
            {({ values, errors, submitCount, handleChange, handleBlur }) => (
              <Form>
                {error && <ErrorText style={{ marginBottom: 12 }}>{error}</ErrorText>}
                <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 12 }}>Enter the 6-digit code from your authenticator app.</p>
                <Label htmlFor="patient_mfa_code">Code</Label>
                <Input
                  id="patient_mfa_code"
                  type="text"
                  name="code"
                  placeholder="000000"
                  value={values.code ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="one-time-code"
                />
                {submitCount > 0 && errors.code && <ErrorText>{errors.code}</ErrorText>}
                <SubmitButton type="submit">Verify</SubmitButton>
                <TermsLink type="button" onClick={() => { setPatientMfaStep(false); setError(''); }} style={{ display: 'block', marginTop: 12, background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 14 }}>
                  Back
                </TermsLink>
              </Form>
            )}
          </Formik>
        )}
        {!mfaStep && !patientMfaStep && loginMode === 'provider' && <BackLink to="/signup" style={{ marginTop: 8 }}>Create account</BackLink>}
      </Card>
      <TermsAndConditionsModal
        show={showTermsModal}
        onHide={() => setShowTermsModal(false)}
        onAgree={() => {
          setTermsAgreementStatus(true, termsVersion);
          setShowTermsModal(false);
          setError('');
        }}
        termsVersion={termsVersion}
      />
    </Wrapper>
  );
};

export default Login;
